<template>
    <div class="reservation-view position-relative">
        <div class="containter">
            <div class="row mb-3">
                <div class="col-md-9">
                    <h1 class="page-title">
                        <SidebarButton :dashboardUrl="dashboardUrl" />
                        Calendário de reservas
                    </h1>
                </div>
                <div class="col-md-3">
                    <button class="btn btn-primary btn-block" v-if="shouldDisplayCreateButton" @click="handleNewReservation">Nova reserva</button>
                </div>
            </div>
        </div>
        <div class="calendar-loading" v-if="loading">
            <i class="material-icons loading-icon-size">cached</i>
        </div>
        <ProductFilter class="mt-2" v-if="shouldDisplayProductFilter" @onSelectedProducts="handleSelectedProducts" :products='availableProducts' :loading="loadingProducts"></ProductFilter>
        <ReservationForm
            v-if="newReservation"
            :products="availableProducts"
            :reservation="newReservation"
            :on-cancel="cancelNewReservation"
            :on-submit="handleReservationSubmit"
        ></ReservationForm>
        <DailyView
            v-else-if="selectedDate"
            :date="selectedDate"
            :reservations="selectedDateReservations"
            :on-close="handleDateUnset"
            :reference-date="now"
            :minute-is-now="minuteIsNow"
            :products="availableProducts"
            :on-reservation-submit="handleReservationSubmit"
            :on-reservation-deletion="handleReservationDeletion"
            :on-reservation-reschedule="handleReservationReschedule"
            :on-reservation-reallocation="handleReservationReallocation"
            :voucher-page-url="voucherPageUrl"
            :highlighted-reservation="highlightedReservation"
            :ack-highlighted-reservation="acknowledgeHighlightedReservation"
            :minute-set-interval="minuteSetInterval"
        ></DailyView>
        <CalendarView
            v-else
            :initial-month="currentMonth"
            :reference-date="now"
            :on-month-change="handleMonthChange"
            :on-date-selection="handleDateSelection"
            :render-date="renderDate"
        ></CalendarView>
    </div>
</template>

<script>
import { requestJson, requestAllPagesJson, parseJwt } from '../helpers';
import { dateFromDateString } from '../../common/calendar';
import DailyView from './DailyView';
import ReservationForm from './ReservationForm.vue';
import ProductFilter from './ProductFilter.vue';
import CalendarView from './CalendarView';
import DisplayCalendar from './mixins/DisplayCalendar';
import RequestCount from './mixins/RequestCount';
import SidebarButton from './SidebarButton';

export default {
    mixins: [
        DisplayCalendar,
        RequestCount
    ],
    components: {
        DailyView,
        ReservationForm,
        ProductFilter,
        CalendarView,
        SidebarButton,
    },
    props: {
        monthSearchUrl: String,
        dateSearchUrl: String,
        availableProductsUrl: String,
        storeReservationUrl: String,
        voucherPageUrl: String,
        highlightedReservationUrl: String,
        minuteSetInterval: Number,
        dashboardUrl: String,
    },
    data(){
        return {
            selectedDate: null,
            highlightedReservation: null,
            chosenProducts: [],

            dailyIndex: {},
            availableProducts: [],
            newReservation: null,
            loadingProducts: false,
        };
    },
    computed: {
        selectedDateReservations(){
            const data = this.selectedDateData();
            return data ? data.reservations : [];
        },
        shouldDisplayProductFilter(){
            return !this.newReservation;
        },
        shouldDisplayCreateButton(){
            return !this.newReservation;
        },
    },
    methods: {
        async requestMonthData(){
            const token = window.accessToken;

            if (token) {
                const parsedToken = parseJwt(token);
                const expirationDate = new Date(parsedToken.exp * 1000);
    
                if (expirationDate < new Date()) {
                    return;
                }
            }

            if (this.currentInterval == null) {
                return;
            }

            const [
                { dateString: from },
                { dateString: to }
            ] = this.currentInterval;

            const id_products = this.chosenProducts.join(',');
            const params = { from, to, id_products };

            this.addRequestCount();

            try {
                const arr = await requestJson(this.monthSearchUrl, params);

                this.monthlyIndex = arr.reduce((carry, { date, products, vouchers }) => ({
                    ...carry,
                    [date]: [products, vouchers]
                }), {});
            } catch(err) {
                console.error(err);
            } finally {
                this.subRequestCount();
            }
        },

        renderDate(date, createElement){
            const data = this.monthlyIndex[date.dateString];

            if(!data){
                return null;
            }

            const [productMap, vouchers] = data;
            const products = Object.values(productMap);

            const listProducts = products.map(({ name, quantity }) => createElement('li', [
                createElement('span', { 'class': 'product' }, name),
                createElement('span', { 'class': 'quantity u-pull-right' }, quantity)
            ]));

            let listVouchers = [];

            if(vouchers){
                const {consumed, total} = vouchers;
                listVouchers = createElement('div', [
                    createElement('span', { 'class': 'vouchers' }, `Consumidas (${consumed}/${total})`)
                ]);
            }

            return createElement('div', [
                createElement('ul', listProducts),
                listVouchers,
            ]);
        },

        handleDateSelection(date){
            this.selectedDate = date;

            this.requestDateReservations(date);
        },
        handleDateUnset(){
            this.selectedDate = null;
        },

        async requestDateReservations(date){
            const token = window.accessToken;

            if (token) {
                const parsedToken = parseJwt(token);
                const expirationDate = new Date(parsedToken.exp * 1000);
    
                if (expirationDate < new Date()) {
                    return;
                }
            }

            const params = {
                date: date.dateString,
                id_products: this.chosenProducts.join(','),
            };

            this.addRequestCount();

            try {
                const data = await requestJson(this.dateSearchUrl, params);
                this.dailyIndex = { ...this.dailyIndex, [date.dateString]: data };
            } finally {
                this.subRequestCount();
            }
        },

        minuteIsNow(min, duration){
            const hourDuration = 60;
            const hour = Math.floor(min / hourDuration);
            const minutes = min % hourDuration;

            const mins = this.now.getMinutes();
            return this.now.getHours() === hour
                && mins >= minutes
                && mins < minutes + duration;
        },

        requestHighlightedReservation(){
            if(!this.highlightedReservationUrl)
                return;

            requestJson(this.highlightedReservationUrl)
                .then(json => this.receiveHighlightedReservation(json))
                .catch(ex => this.subRequestCount());

            this.addRequestCount();
        },
        receiveHighlightedReservation({ reservation }){
            if(reservation.schedule_date){
                this.highlightedReservation = reservation;

                const scheduleDate = dateFromDateString(reservation.schedule_date);
                this.handleDateSelection(scheduleDate);
            }

            this.subRequestCount();
        },
        acknowledgeHighlightedReservation(){
            this.highlightedReservation = null;
        },

        async requestAvailableProducts(){
            this.addRequestCount();
            this.loadingProducts = true;

            try {
                this.availableProducts = await requestAllPagesJson(this.availableProductsUrl);
            } finally {
                this.subRequestCount();

                this.loadingProducts = false;
            }
        },

        handleNewReservation(schedule_time_minutes = ''){
            this.newReservation = {
                data: { schedule_time_minutes },
                validation: null
            };
        },
        cancelNewReservation(){
            this.newReservation = null;
        },
        handleReservationSubmit(formData){
            requestJson(this.storeReservationUrl, formData, 'POST')
                .then(json => this.receiveReservationResponse(json))
                .catch(ex => null);

            this.newReservation = { ...this.newReservation, data: formData };
            this.addRequestCount();
        },

        receiveReservationResponse(json){
            this.subRequestCount();

            if (json.status === 'OK') {
                this.newReservation = null;

                this.forceReload();
            } else {
                this.newReservation = { ...this.newReservation, validation: json.errors };
            }
        },
        handleReservationDeletion(url, callback){
            requestJson(url, null, 'DELETE')
                .then(json => this.receiveReservationDeletionResponse(json, callback))
                .catch(ex => null);

            this.addRequestCount();
        },
        receiveReservationDeletionResponse(json, callback){
            if(json.status === 'ok'){
                setTimeout(() => this.requestDateReservations(this.selectedDate), 0);
            }

            this.subRequestCount();

            callback(json);
        },

        async forceReload(){
            if (this.newReservation) {
                return;
            }

            await this.requestMonthData();

            if (this.selectedDate) {
                await this.requestDateReservations(this.selectedDate);
            }
        },

        selectedDateData(){
            const date = this.selectedDate;
            if(date && this.dailyIndex[date.dateString])
                return this.dailyIndex[date.dateString];

            return null;
        },

        async handleReservationReschedule(url, scheduleDate, scheduleTime){
            const body = new FormData();
            body.set('schedule_date', scheduleDate);
            body.set('schedule_time_minutes', scheduleTime);

            await this.postFormData(url, body);
        },
        async handleReservationReallocation(url, ids, verifications = {}){
            const body = new FormData();
            body.set('resource_ids', ids);

            for (const [name, value] of Object.entries(verifications)) {
                body.set(`verifications[${name}]`, value ? '1' : '0');
            }

            await this.postFormData(url, body);
        },
        async postFormData(url, body){
            this.addRequestCount();
            let json = null;

            try {
                json = await requestJson(url, body, 'POST');
            } catch (err) {
                console.error(err);
            } finally {
                this.subRequestCount();
            }

            if (json && json.reservation) {
                this.requestDateReservations(this.selectedDate);
            }
        },

        handleSelectedProducts(products){
            this.chosenProducts = products.map(p => p.id);
            this.forceReload();
        }
    },
    async mounted(){
        await this.requestAvailableProducts();
        this.requestHighlightedReservation();

        setInterval(() => this.now = new Date(), 1000 * 60);

        // Polls every 15s while the tab is visible. A hidden tab stops polling and
        // reloads once when it becomes visible again.
        const intervalMs = 1000 * 15;
        let reloadTimeout = null;
        const execReload = async () => {
            if (document.hidden) {
                reloadTimeout = null;
                return;
            }

            await this.forceReload();
            reloadTimeout = setTimeout(execReload, intervalMs);
        };
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && reloadTimeout === null) {
                execReload();
            }
        });
        reloadTimeout = setTimeout(execReload, intervalMs);
    }
}
</script>

<style lang="scss">
.reservation-view {
    .date-anchor {
        ul {
            margin: 0;
            padding: 0;
            list-style: none;
            font-size: .75rem;
        }
        li {
            display: flex;
            margin: .5rem 0 0;
            line-height: 23px;
        }
        .product {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .quantity {
            display: inline-block;
            margin-left: 5px;
            width: 23px;
            height: 23px;
            border-radius: 50%;
            text-align: center;
            line-height: 24px;
            background-color: #131552;
            color: #fff;
        }
        .vouchers {
            display: flex;
            font-size: .75rem;
            font-weight: bolder;
            margin: .5rem 0 0;
        }
    }
}
</style>
